import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import Sidebar from "../components/Sidebar";
import {
  Users, FileText, Receipt, ChartLine, CurrencyInr, Warning, Clock, Funnel,
  CheckCircle, PlusCircle, ListChecks, TrendUp, Package, Target, ShieldCheck, FileArrowDown,
} from "@phosphor-icons/react";

const fmtINR = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;

const Tile = ({ label, value, sub, icon: Icon, color = "#002FA7", onClick, testId }) => (
  <button data-testid={testId} onClick={onClick} type="button"
    className={`text-left bg-white p-5 rounded border border-slate-200 hover:border-slate-400 hover:shadow-sm transition-all ${onClick ? "cursor-pointer" : "cursor-default"}`}>
    <div className="flex items-start justify-between">
      <div className="flex-1">
        <p className="text-xs uppercase tracking-wider font-bold text-slate-500">{label}</p>
        <p className="text-2xl font-bold text-slate-900 mt-1" style={{ fontFamily: "Outfit, sans-serif" }}>{value}</p>
        {sub && <p className="text-xs text-slate-400 mt-0.5">{sub}</p>}
      </div>
      <div className="p-2 rounded-full" style={{ background: color + "15" }}>
        <Icon size={22} weight="duotone" color={color} />
      </div>
    </div>
  </button>
);

const QuickAction = ({ label, icon: Icon, to, navigate }) => (
  <button onClick={() => navigate(to)} type="button"
    className="flex flex-col items-center gap-2 p-4 bg-white border border-slate-200 rounded hover:border-[#002FA7] hover:text-[#002FA7] transition-all">
    <Icon size={24} weight="duotone" />
    <span className="text-xs font-semibold">{label}</span>
  </button>
);

const RoleDashboard = ({ user, logout }) => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const role = user?.role || "customer";

  useEffect(() => {
    const endpoint = {
      bde: `/dashboards/bde?user_id=${user?.user_id}`,
      bdm: `/dashboards/bdm`,
      manager: `/dashboards/manager`,
      admin: `/dashboards/admin-billing`,
      marketing_head: `/dashboards/manager`,
      cfo: `/dashboards/manager`,
    }[role];
    if (!endpoint) { setLoading(false); return; }
    axios.get(`${API}${endpoint}`).then(r => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [role, user?.user_id]);

  if (loading) return (
    <div className="min-h-screen bg-slate-50"><Sidebar user={user} logout={logout} /><div className="ml-64 p-12">Loading your dashboard…</div></div>
  );

  return (
    <div className="min-h-screen bg-slate-50" data-testid="role-dashboard">
      <Sidebar user={user} logout={logout} />
      <div className="ml-64">
        <div className="bg-white border-b border-slate-200 px-6 py-5">
          <p className="text-xs uppercase tracking-widest font-bold text-slate-500">Dashboard · {role.replace(/_/g, " ").toUpperCase()}</p>
          <h1 className="text-2xl font-bold text-slate-900 mt-0.5" style={{ fontFamily: "Outfit, sans-serif" }}>
            Welcome, {user?.name?.split(" ")[0] || "there"} 👋
          </h1>
        </div>

        <div className="p-6 space-y-6">
          {role === "bde" && data && (
            <>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                <Tile label="My Leads" value={data.my_leads || 0} icon={Users} testId="kpi-my-leads" onClick={() => navigate("/leads")} />
                <Tile label="Quotes pending" value={data.my_quotes_pending || 0} icon={Clock} color="#f59e0b" testId="kpi-quotes-pending" onClick={() => navigate("/billing")} />
                <Tile label="Today's Tasks" value={data.today_tasks || 0} icon={ListChecks} color="#8b5cf6" testId="kpi-today-tasks" onClick={() => navigate("/crm/tasks")} />
                <Tile label="Overdue Collections" value={fmtINR(data.overdue_collections)} icon={Warning} color="#ef4444" testId="kpi-overdue-collections" />
                <Tile label="MTD Billing" value={fmtINR(data.mtd_billing)} icon={CurrencyInr} color="#10b981" testId="kpi-mtd-billing" />
              </div>
              <QuickActions role={role} navigate={navigate} />
            </>
          )}

          {role === "bdm" && data && (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Tile label="Team Pipeline" value={data.team_pipeline_count || 0} sub={fmtINR(data.team_pipeline_value)} icon={Funnel} testId="kpi-pipeline" onClick={() => navigate("/crm/deals")} />
                <Tile label="Active Quotes" value={fmtINR(data.team_quote_value)} icon={FileText} color="#8b5cf6" testId="kpi-quote-value" onClick={() => navigate("/billing")} />
                <Tile label="Pending Approvals" value={data.pending_approvals || 0} icon={ShieldCheck} color="#f59e0b" testId="kpi-pending" onClick={() => navigate("/approvals")} />
                <Tile label="Team Overdue" value={fmtINR(data.team_overdue_exposure)} icon={Warning} color="#ef4444" testId="kpi-team-overdue" />
              </div>
              {data.top_accounts?.length > 0 && (
                <div className="bg-white border border-slate-200 rounded p-5">
                  <h3 className="font-bold text-sm mb-3">🏢 Top Accounts by Outstanding</h3>
                  <table className="w-full text-sm">
                    <thead><tr className="text-xs text-slate-500 uppercase">
                      <th className="text-left p-2">Customer</th>
                      <th className="text-right p-2">Outstanding</th>
                    </tr></thead>
                    <tbody>
                      {data.top_accounts.map(a => (
                        <tr key={a.customer_code} className="border-t border-slate-100">
                          <td className="p-2">{a.customer_code} · {a.name}</td>
                          <td className="p-2 text-right font-bold text-red-600">{fmtINR(a.outstanding)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              <QuickActions role={role} navigate={navigate} />
            </>
          )}

          {(role === "manager" || role === "cfo" || role === "marketing_head") && data && (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Tile label="Gross Billing (MTD)" value={fmtINR(data.gross_billing)} icon={CurrencyInr} color="#10b981" testId="kpi-gross-billing" />
                <Tile label="Collections" value={fmtINR(data.collections)} sub={`${data.collections_ratio || 0}% of billed`} icon={ChartLine} color="#059669" testId="kpi-collections" />
                <Tile label="Blocked Accounts" value={data.blocked_accounts || 0} icon={Warning} color="#ef4444" testId="kpi-blocked" />
                <Tile label="Approval Bottlenecks" value={data.approval_bottlenecks || 0} sub="Stuck > 3 days" icon={Clock} color="#f59e0b" testId="kpi-bottlenecks" onClick={() => navigate("/approvals")} />
              </div>
              {data.aging_totals && (
                <div className="bg-white border border-slate-200 rounded p-5">
                  <h3 className="font-bold text-sm mb-3">📊 Aging Heatmap</h3>
                  <div className="grid grid-cols-5 gap-2 text-center">
                    {[
                      { label: "Current", key: "current", color: "#10b981" },
                      { label: "0-30", key: "d0_30", color: "#f59e0b" },
                      { label: "31-60", key: "d31_60", color: "#f97316" },
                      { label: "61-90", key: "d61_90", color: "#ef4444" },
                      { label: "90+", key: "d90_plus", color: "#dc2626" },
                    ].map(b => (
                      <div key={b.key} className="p-3 rounded" style={{ background: b.color + "15" }}>
                        <p className="text-xs text-slate-500 uppercase font-bold">{b.label}</p>
                        <p className="text-lg font-bold" style={{ color: b.color }}>{fmtINR(data.aging_totals[b.key])}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <QuickActions role={role} navigate={navigate} />
            </>
          )}

          {role === "admin" && data && (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Tile label="Total Users" value={data.total_users || 0} icon={Users} testId="kpi-users" onClick={() => navigate("/user-management")} />
                <Tile label="Active (24h)" value={data.active_users_24h || 0} icon={TrendUp} color="#10b981" testId="kpi-active" />
                <Tile label="Missing GSTIN" value={data.customers_missing_gst || 0} sub="Enterprise/dealer" icon={Warning} color="#f59e0b" testId="kpi-missing-gst" onClick={() => navigate("/billing")} />
                <Tile label="Missing HSN" value={data.items_missing_hsn || 0} sub="On products" icon={Package} color="#f59e0b" testId="kpi-missing-hsn" onClick={() => navigate("/products")} />
                <Tile label="Audit Exceptions (24h)" value={data.audit_exceptions_24h || 0} icon={ShieldCheck} color="#ef4444" testId="kpi-audit" />
                <Tile label="Marketing Approvals" value={data.pending_approvals?.marketing || 0} icon={Target} color="#8b5cf6" testId="kpi-mktg-approvals" onClick={() => navigate("/marketing-campaigns")} />
                <Tile label="Billing Approvals" value={data.pending_approvals?.billing || 0} icon={FileText} color="#f59e0b" testId="kpi-billing-approvals" onClick={() => navigate("/approvals")} />
              </div>
              <QuickActions role={role} navigate={navigate} />
            </>
          )}

          {role === "customer" && (
            <div className="bg-white border border-slate-200 rounded p-8 text-center">
              <Package size={48} className="mx-auto text-slate-400 mb-3" weight="duotone" />
              <h2 className="text-xl font-bold">Welcome</h2>
              <p className="text-slate-500 mt-2">Browse products in our security marketplace.</p>
              <button onClick={() => navigate("/products")} className="mt-4 px-5 py-2 bg-[#002FA7] text-white rounded">Browse Products</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const QuickActions = ({ role, navigate }) => {
  const actions = {
    bde: [
      { label: "New Lead", icon: PlusCircle, to: "/leads" },
      { label: "New Quotation", icon: FileText, to: "/billing" },
      { label: "My Tasks", icon: ListChecks, to: "/crm/tasks" },
      { label: "View Customers", icon: Users, to: "/billing" },
    ],
    bdm: [
      { label: "Team Approvals", icon: ShieldCheck, to: "/approvals" },
      { label: "Sales Orders", icon: Package, to: "/billing" },
      { label: "Marketing Campaigns", icon: Target, to: "/marketing-campaigns" },
      { label: "Team Performance", icon: TrendUp, to: "/team-performance" },
    ],
    manager: [
      { label: "Pending Approvals", icon: ShieldCheck, to: "/approvals" },
      { label: "Aging Report", icon: Warning, to: "/billing" },
      { label: "Advanced Reports", icon: ChartLine, to: "/billing" },
      { label: "Marketing", icon: Target, to: "/marketing-campaigns" },
    ],
    admin: [
      { label: "User Management", icon: Users, to: "/user-management" },
      { label: "Billing", icon: Receipt, to: "/billing" },
      { label: "Audit Logs", icon: ShieldCheck, to: "/approvals" },
      { label: "Enquiries", icon: FileArrowDown, to: "/admin/enquiries" },
    ],
    marketing_head: [
      { label: "Campaigns", icon: Target, to: "/marketing-campaigns" },
      { label: "Approvals", icon: ShieldCheck, to: "/approvals" },
      { label: "Social Hub", icon: ChartLine, to: "/social-hub" },
      { label: "Analytics", icon: TrendUp, to: "/analytics" },
    ],
    cfo: [
      { label: "Aging", icon: Warning, to: "/billing" },
      { label: "Reports", icon: ChartLine, to: "/reports" },
      { label: "Approvals", icon: ShieldCheck, to: "/approvals" },
      { label: "Billing", icon: Receipt, to: "/billing" },
    ],
  }[role] || [];
  if (actions.length === 0) return null;
  return (
    <div>
      <p className="text-xs uppercase tracking-widest font-bold text-slate-500 mb-2">Quick Actions</p>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {actions.map(a => <QuickAction key={a.label} {...a} navigate={navigate} />)}
      </div>
    </div>
  );
};

export default RoleDashboard;
